import { Box, Stack, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import {
  NavMenuList,
  MenuLinkItem,
  MenuSection,
  PanelSplitter,
  SideNav,
  SideNavCollapseButton,
} from '@atlassian/navigation-system/v4';
import React, { KeyboardEvent, MouseEvent, useCallback } from 'react';
import { FormattedMessage } from 'react-intl-next';
import { useRouter } from 'react-resource-router';

import { useAnalytics } from '@townsquare/analytics';
import { localStorageFacade } from '@townsquare/facade';
import { matchRoute } from '@townsquare/router/primitives';
import { openInNewTab } from '@townsquare/ui-interactions';

import { GiveFeedbackButton } from './GiveFeedbackButton';
import { SwitcherLinks } from './SwitcherLinks';
import { useSideNavItems } from './use-side-nav-items';

// To compensate for the collapse button that is position: absolute on the first item
const firstSideNavItemMarginStyle = xcss({ marginInlineEnd: 'space.400' });

const NAV_COLLAPSED_LS_KEY = 'is-griffing-nav-collapsed';

export const GriffinSidebarNavigation = () => {
  const analytics = useAnalytics();
  const [routerStore, routerActions] = useRouter();
  const items = useSideNavItems();
  const defaultCollapsed = localStorageFacade.getItem(NAV_COLLAPSED_LS_KEY) === 'true';

  const onClick = useCallback(
    (item: (typeof items)[0], event: MouseEvent | KeyboardEvent) => {
      event.preventDefault();
      if (event.metaKey) {
        openInNewTab(item.path);
      } else {
        void routerActions.push(item.path);
      }
      void analytics.ui('sideNavItem', 'clicked', {
        screen: routerStore.route.name,
        navName: item.name,
        navPath: item.path,
      });
    },
    [analytics, routerActions, routerStore.route.name],
  );

  const onToggle = (isCollapsed: boolean) => {
    localStorageFacade.setItem(NAV_COLLAPSED_LS_KEY, isCollapsed.toString());
  };

  return (
    <SideNav
      defaultWidth={300}
      onCollapse={() => onToggle(true)}
      onExpand={() => onToggle(false)}
      defaultCollapsed={defaultCollapsed}
      collapseButton={<SideNavCollapseButton />}
    >
      <NavMenuList>
        <Stack space="space.200">
          <MenuSection>
            {items.map((item, index) => {
              const isSelected = Boolean(matchRoute(item.matches, routerStore.location.pathname, undefined));

              const thisItem = (
                <MenuLinkItem
                  key={item.key}
                  onClick={e => onClick(item, e)}
                  isSelected={isSelected}
                  href={item.path}
                  elemBefore={<item.Icon label="" color={isSelected ? token('color.icon.selected') : undefined} />}
                  data-testid={item.testId}
                >
                  {item.name}
                </MenuLinkItem>
              );

              return index === 0 ? <Box xcss={firstSideNavItemMarginStyle}>{thisItem}</Box> : thisItem;
            })}
          </MenuSection>
          <SwitcherLinks />
        </Stack>
      </NavMenuList>
      <GiveFeedbackButton />
      <PanelSplitter
        label={
          <FormattedMessage
            id="townsquare.navigation.sidebar.resize"
            defaultMessage="Resize side navigation"
            description="Accessible label of the button in the sidebar's resizing component"
          />
        }
      />
    </SideNav>
  );
};
